/**
 * Rohstoffe actions.
 * @module actions/Rohstoff
 */

import {
  GET_ROHSTOFFE,
  GET_ROHSTOFFBYID,
  CREATE_ROHSTOFF,
  UPDATE_ROHSTOFF,
  DELETE_ROHSTOFF,
  GET_ROHSTOFFE_NAMES,
  CHECK_ROHSTOFFE,
  GET_ROHSTOFF_HISTORY,
  GET_ROHSTOFF_VERSION,
  RESTORE_ROHSTOFF_VERSION,
  COMPARE_ROHSTOFF_VERSIONS,
  RESET_ROHSTOFF_HISTORY,
} from '../../constants/ActionTypes';

import isEmpty from 'lodash/isEmpty';

/**
 * Get Rohstoff items.
 * @function getRohstoffe
 * @param {string} id Rohstoff id
 * @returns {Object} Rohstoff action.
 */
export function getRohstoffe({ showDeleted = false, batchingParameters = {} }) {
  const {
    limit = 10,
    skip = 0,
    sort_by = '',
    asc = false,
    searchTerms = '',
    searchField = {},
    filters = {},
  } = batchingParameters;
  // Initialize an empty array to store query parameters
  const queryParams = [];
  if (sort_by) {
    queryParams.push(`sort_by=${sort_by}`);
  }
  if (searchTerms) {
    queryParams.push(`search=${encodeURIComponent(searchTerms)}`);
  }
  if (searchField.value) {
    queryParams.push(`search_kategorie=${searchField.value}`);
  }
  // Construct the pathSuffix by joining query parameters with '&'
  let pathSuffix = `?limit=${limit}&skip=${skip}&asc=${asc}`;
  if (queryParams.length > 0) {
    pathSuffix += `&${queryParams.join('&')}`;
  }

  //Apply filters from batching params
  Object.entries(filters).map((filter) => {
    const filterValue = filter[1];
    let filterValues;
    if (!isEmpty(filterValue)) {
      if (Array.isArray(filter[1])) {
        filterValues = filterValue.map((singleValue) => {
          return singleValue.value;
        });
      } else if (typeof filterValue === 'object') {
        filterValues = filterValue.value;
      } else if (typeof filterValue === 'number') {
        filterValues = filterValue;
      } else if (typeof filterValue === 'boolean') {
        if (filterValue === true) {
          filterValues = 1;
        } else {
          filterValues = 0;
        }
      }

      pathSuffix += `&${filter[0]}=${filterValues}`;
    }
    return false;
  });

  if (showDeleted) {
    return {
      type: GET_ROHSTOFFE,
      request: {
        op: 'get',
        path: `/@rohstoffe/deleted${pathSuffix}`,
      },
    };
  } else {
    return {
      type: GET_ROHSTOFFE,
      request: {
        op: 'get',
        path: `/@rohstoffe${pathSuffix}`,
      },
    };
  }
}

/**
 * Get Rohstoff items.
 * @function getRohstoffeNames
 * @returns {Object} Rohstoff action.
 */
export function getRohstoffeNames() {
  return {
    type: GET_ROHSTOFFE_NAMES,
    request: {
      op: 'get',
      path: `/@rohstoffe/names`,
    },
  };
}

/**
 * Check Rohstoffe for specific value in specific field.
 * @function checkProdukte
 * @param {string} field, specified field
 * @param {string} value, specified value
 * @returns {Object} Rohstoffe action.
 */
export function checkRohstoffe(field, value) {
  return {
    type: CHECK_ROHSTOFFE,
    request: {
      op: 'get',
      path: `/@rohstoffe_check/${field}=${value}`,
    },
  };
}

/**
 * Get Benutzer item.
 * @function getRohstoffByID
 * @param {string} id Benutzer id
 * @returns {Object} Benutzer action.
 */
export function getRohstoffByID(id) {
  return {
    type: GET_ROHSTOFFBYID,
    request: {
      op: 'get',
      path: `/@rohstoffe/${id}`,
    },
  };
}

/**
 * Create a new Rohstoff.
 * @function createRohstoff
 * @param {Object} formData The data of the new Rohstoff
 * @returns {Object} Rohstoff action.
 */
export function createRohstoff(formData) {
  return {
    type: CREATE_ROHSTOFF,
    request: {
      op: 'post',
      path: '/@rohstoffe',
      data: formData,
    },
  };
}

/**
 * Update a new Rohstoff.
 * @function createRohstoff
 * @param {Object} formData The data of the new Rohstoff
 * @returns {Object} Rohstoff action.
 */
export function updateRohstoff(id, formData) {
  return {
    type: UPDATE_ROHSTOFF,
    request: {
      op: 'patch',
      path: `/@rohstoffe/${id}`,
      data: formData,
    },
  };
}

/**
 * Delete a Rohstoff.
 * @function deleteRohstoff
 * @returns {Object} Success Message;
 */
export function deleteRohstoff(id) {
  return {
    type: DELETE_ROHSTOFF,
    request: {
      op: 'del',
      path: `/@rohstoffe/${id}`,
    },
  };
}

/**
 * Force delete a Rohstoff.
 * @function deleteRohstoffForce
 * @returns {Object} Success Message;
 */
export function deleteRohstoffForce(id) {
  return {
    type: DELETE_ROHSTOFF,
    request: {
      op: 'put',
      path: `/@rohstoffe_force_delete/${id}`,
    },
  };
}

/**
 * Get version history for a Rohstoff.
 * @function getRohstoffHistory
 * @param {string} id Rohstoff id
 * @param {number} limit Optional limit on number of versions to retrieve
 * @returns {Object} Rohstoff history action.
 */
export function getRohstoffHistory(id, limit = null) {
  const path = limit
    ? `/@rohstoffe-history/${id}?limit=${limit}`
    : `/@rohstoffe-history/${id}`;
  return {
    type: GET_ROHSTOFF_HISTORY,
    request: {
      op: 'get',
      path: path,
    },
  };
}

/**
 * Get a specific version of a Rohstoff.
 * @function getRohstoffVersion
 * @param {string} id Rohstoff id
 * @param {number} versionNumber Version number to retrieve
 * @returns {Object} Rohstoff version action.
 */
export function getRohstoffVersion(id, versionNumber) {
  return {
    type: GET_ROHSTOFF_VERSION,
    request: {
      op: 'get',
      path: `/@rohstoffe-history/${id}/version/${versionNumber}`,
    },
  };
}

/**
 * Restore a Rohstoff to a specific version.
 * @function restoreRohstoffVersion
 * @param {string} id Rohstoff id
 * @param {number} versionNumber Version number to restore
 * @param {string} reason Optional reason for restoration
 * @returns {Object} Restore action.
 */
export function restoreRohstoffVersion(id, versionNumber, reason = '') {
  return {
    type: RESTORE_ROHSTOFF_VERSION,
    request: {
      op: 'post',
      path: `/@rohstoffe-restore/${id}/version/${versionNumber}`,
      data: { reason },
    },
  };
}

/**
 * Compare two versions of a Rohstoff.
 * @function compareRohstoffVersions
 * @param {string} id Rohstoff id
 * @param {number} version1 First version number
 * @param {number} version2 Second version number
 * @returns {Object} Compare action.
 */
export function compareRohstoffVersions(id, version1, version2) {
  return {
    type: COMPARE_ROHSTOFF_VERSIONS,
    request: {
      op: 'get',
      path: `/@rohstoffe-diff/${id}/v1/${version1}/v2/${version2}`,
    },
  };
}

/**
 * Reset rohstoff history state.
 * @function resetRohstoffHistory
 * @returns {Object} Reset action.
 */
export function resetRohstoffHistory() {
  return {
    type: RESET_ROHSTOFF_HISTORY,
  };
}
